import { Skeleton } from '@chakra-ui/react';

import type { GetSentimentDataQuery } from '@revelio/data-access';

import type { CompanyMetrics } from '../hooks/useCompetitorComposition';
import type { EmployerHealth } from '../hooks/useEmployerHealth';
import type { RoleLevel } from '../hooks/useResolveProfileIds';
import { SectionCard } from '../SectionCard';
import { type CareerMoveStep, CareerMovesInsight } from './CareerMovesInsight';
import { EmployerHealthSummary } from './EmployerHealthSummary';
import { GrowthProjectionCard } from './GrowthProjectionCard';

interface EmployerOutlookSectionProps {
  currentEmployerHealth: EmployerHealth | undefined;
  companyName: string | null;
  sentimentEffectsData: GetSentimentDataQuery | undefined;
  sentimentEffectsLoading: boolean;
  currentCompanyMetrics: CompanyMetrics | undefined;
  competitorMetrics: CompanyMetrics[];
  sentimentOverall: number | null;
  careerMoveSteps: CareerMoveStep[];
  userSalary: number;
  userRoleLevel: RoleLevel | null;
  isDataLoading: boolean;
  isResolving: boolean;
  metrics: CompanyMetrics[];
}

export const EmployerOutlookSection = ({
  currentEmployerHealth,
  companyName,
  sentimentEffectsData,
  sentimentEffectsLoading,
  currentCompanyMetrics,
  competitorMetrics,
  sentimentOverall,
  careerMoveSteps,
  userSalary,
  userRoleLevel,
  isDataLoading,
  isResolving,
  metrics,
}: EmployerOutlookSectionProps) => {
  if (isResolving) {
    return (
      <SectionCard heading="Employer Outlook" spacing={4}>
        <Skeleton height="140px" borderRadius="md" />
        <Skeleton height="200px" borderRadius="md" />
      </SectionCard>
    );
  }

  return (
    <SectionCard heading="Employer Outlook" spacing={6}>
      <CareerMovesInsight
        steps={careerMoveSteps}
        isLoading={isDataLoading}
        metrics={metrics}
        userSalary={userSalary}
        userRoleLevel={userRoleLevel}
      />

      <EmployerHealthSummary
        health={currentEmployerHealth}
        companyName={companyName}
        sentimentEffectsData={sentimentEffectsData}
        sentimentEffectsLoading={sentimentEffectsLoading}
        isLoading={isDataLoading}
        currentCompanyMetrics={currentCompanyMetrics}
        sentimentOverall={sentimentOverall}
        userSalary={userSalary}
        userRoleLevel={userRoleLevel}
      />

      <GrowthProjectionCard
        currentCompanyMetrics={currentCompanyMetrics}
        competitorMetrics={competitorMetrics}
        companyName={companyName}
        userSalary={userSalary}
        isLoading={isDataLoading}
      />
    </SectionCard>
  );
};
